import { Fragment, useEffect, useState } from "react";
import { NavLink, useLocation, useParams } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { Menu, Transition } from "@headlessui/react";
import NewToolTip from "./New-ToolTip";
import { useDispatch, useSelector } from "react-redux";

export const ChartNav = () => {
  const { id, serviceId } = useParams();
  const location = useLocation();
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const { clinicalServicesSmall, clinicalServiceName } = useSelector(
    (state) => state.clinicalService
  );
  const { minorAilments, medicationReviews } = clinicalServicesSmall;
  const [isExitingService, setIsExitingService] = useState(false);

  useEffect(() => {
    const iconBases = ["user-details", "rx", "medicine", "injection", "measurement", "intervention", "med-summary", "chart-icon", "appointment"];

    // Preload icon SVGs to warm the browser cache
    const preloadIcons = () => {
      const states = ['default', 'hover', 'select'];
      iconBases.forEach((iconBase) => {
        states.forEach((state) => {
          const img = new Image();
          img.src = `/icons/${iconBase}-${state}.svg`;
        });
      });
    };

    preloadIcons();
  }, []);

  // Reusable class names
  const buttonBaseClasses = "relative flex items-center justify-center w-12 h-12 transition-all hover:rounded-r-none rounded 2xl:w-11.5 2xl:h-11.5 group overflow-hidden hover:overflow-visible disabled:cursor-not-allowed";
  const backgroundDivClasses = "absolute inset-0 rounded transition-colors";
  const iconContainerClasses = "relative w-full h-full flex items-center justify-center flex-shrink-0";
  const iconClasses = "relative z-10 [&_svg>path:first-of-type]:fill-transparent";
  const iconVisibilityClasses = "transition-opacity duration-200";

  const chartNav = [
    {
      name: "Patient Details",
      iconBase: "user-details",
      href: `/patient/${id}/`,
    },
    {
      name: "Clinical Details",
      iconBase: "rx",
      href: `/patient/${id}/clinical-details`,
    },
    {
      name: "Medications",
      iconBase: "medicine",
      href: `/patient/${id}/medications/`,
    },
    {
      name: "Vaccines",
      iconBase: "injection",
      href: `/patient/${id}/vaccines/`,
    },
    {
      name: "Measurements - Coming Soon",
      iconBase: "measurement",
      href: `/patient/${id}/measurements/`,
    },
    {
      name: "Interventions",
      iconBase: "intervention",
      href: `/patient/${id}/interventions`,
    },
    {
      name: "Medical Summary",
      iconBase: "med-summary",
      href: `/patient/${id}/medical-summary/NOTES`,
    },
    {
      name: "Charting - Coming Soon",
      iconBase: "chart-icon",
      href: `/patient/${id}/charting/`,
    },
  ];

  const getIconPath = (iconBase, isActive, isHovered) => {
    if (isActive) return `/icons/${iconBase}-select.svg`;
    if (isHovered) return `/icons/${iconBase}-hover.svg`;
    return `/icons/${iconBase}-default.svg`;
  };

  const renderIconStack = (iconBase, isHovered, isActive) => {
    if (isActive) {
      return <ReactSVG src={getIconPath(iconBase, true, false)} className={iconClasses} />;
    }
    const opacity = (visible) => (visible ? "opacity-100" : "opacity-0");
    return (
      <>
        <ReactSVG
          src={getIconPath(iconBase, false, false)}
          className={`${iconClasses} ${iconVisibilityClasses} ${opacity(!isHovered)}`}
        />
        <span className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <ReactSVG
            src={getIconPath(iconBase, false, true)}
            className={`${iconClasses} ${iconVisibilityClasses} ${opacity(isHovered)}`}
          />
        </span>
      </>
    );
  };

  // Check if ANY Minor Ailment service is running (not just the current one)
  const hasRunningMinorAilment = minorAilments?.some(
    (svc) => svc.status === "inProgress"
  );

  // Tabs that should be blocked when Minor Ailment is running
  const blockedTabsForMinorAilments = [
    "Patient Details",
    "Interventions", 
    "Medical Summary",
    "Charting - Coming Soon",
    "Measurements - Coming Soon"
  ];

  const isMinorAilmentActive = minorAilments?.filter((item) => serviceId === item.id)?.length > 0;

  useEffect(() => {
    if (isMinorAilmentActive)
      return setIsExitingService(true);
    else setIsExitingService(false);
  }, [clinicalServicesSmall, isMinorAilmentActive]);

  return (
    <div className="p-2.5 self-stretch min-h-0 bg-gray-20 rounded flex flex-col justify-between relative z-10">
      <div className="flex flex-col gap-2">
        {chartNav.map((item, i) => {
          const isBlockedTab = blockedTabsForMinorAilments.includes(item.name);
          const isMedicalSummaryRoute = location.pathname.includes(
            `/patient/${id}/medical-summary/`
          );
          // When MA service is running: only blocked tabs are enabled, others disabled
          const isDisabled = hasRunningMinorAilment ? !isBlockedTab : (isExitingService && !blockedTabsForMinorAilments.includes(item.name) && isMinorAilmentActive);
          
          const tooltipId = `no-access-${item.name.replace(/\s+/g, '-').toLowerCase()}`;
          
          return item.href === "/" ? (
            <div key={i}>
              <button
                type="button"
                className={`${buttonBaseClasses} bg-transparent ${isDisabled ? 'opacity-50' : ''}`}
                onMouseEnter={() => setHoveredIcon(item.iconBase)}
                onMouseLeave={() => setHoveredIcon(null)}
                disabled={isDisabled}
                data-tooltip-id={isDisabled ? tooltipId : undefined}
              >
                <div className={`${backgroundDivClasses} bg-white group-hover:bg-gray-100 group-hover:border group-hover:border-gray-200`}></div>
                <div className={iconContainerClasses}>
                  {renderIconStack(item.iconBase, hoveredIcon === item.iconBase, false)}
                </div>
                <span
                  className={`absolute w-0 h-8 left-full ml-1 whitespace-nowrap flex items-center rounded-r group-hover:w-auto group-hover:pl-2 group-hover:pr-2 bg-gray-100 text-gray-800`}
                >
                  {item?.name}
                </span>
              </button>
            </div>
          ) : (
            <NavLink 
              to={item.href} 
              key={i}
            >
              {({ isActive }) => (
                <>
                  {(() => {
                    const isCurrentActive = item.name === "Medical Summary" ? isMedicalSummaryRoute : isActive;
                    return (
                  <button
                    type="button"
                    className={`${buttonBaseClasses} ${isCurrentActive ? 'bg-teal-75 text-teal-300' : 'bg-transparent'} ${isDisabled ? 'opacity-50' : ''}`}
                    onMouseEnter={() => setHoveredIcon(item.iconBase)}
                    onMouseLeave={() => setHoveredIcon(null)}
                    disabled={isDisabled}
                    data-tooltip-id={isDisabled ? tooltipId : undefined}
                  >
                    <div className={`${backgroundDivClasses} ${isCurrentActive ? 'bg-teal-75' : 'bg-white group-hover:!bg-gray-100 border border-gray-200'}`}></div>
                    <div className={iconContainerClasses}>
                      {renderIconStack(item.iconBase, hoveredIcon === item.iconBase, isCurrentActive)}
                    </div>
                    {!isDisabled && (
                      <span
                        className={`absolute w-0 h-8 left-full ml-1 whitespace-nowrap flex items-center rounded-r group-hover:w-auto group-hover:pl-2 group-hover:pr-2 ${isCurrentActive ? 'bg-teal-75' : 'bg-gray-100 border border-gray-200'} text-gray-800`}
                      >
                        {item?.name}
                      </span>
                    )}
                  </button>
                    );
                  })()}
                  {isDisabled && (
                    <NewToolTip
                      id={tooltipId}
                      place="right"
                      render={() => (
                        <div className="!bg-white max-w-[300px] text-gray-800 text-body-sm p-2 border border-gray-200 rounded shadow-lg">
                          <p className="text-sm">
                          No access during <br /> minor ailments.
                          </p>
                        </div>
                      )}
                    />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
      <Menu as="div" className="relative inline-block text-left mt-10">
        <div>
          <Menu.Button
            className={`${buttonBaseClasses} mt-2 border-blue-200 border-[1px] bg-transparent`}
            disabled={isExitingService}
          >
            <div className={`${backgroundDivClasses} bg-white group-hover:bg-gray-100 group-hover:border group-hover:border-gray-200`}></div>
            <ReactSVG
              src={getIconPath("appointment", false, false)}
              className={iconClasses}
            />
            <span
              className={`absolute w-0 h-8 left-full ml-1 whitespace-nowrap flex items-center rounded-r group-hover:w-auto group-hover:pl-2 group-hover:pr-2 bg-gray-100 border border-gray-200 text-gray-800`}
            >
              Appointments - Coming Soon
            </span>
          </Menu.Button>
          <NewToolTip
            id={"appointmentTooltip"}
            render={() => (
              <div className="!bg-gray-20 max-w-[460px] text-teal-300 text-body-sm p-2">
                Coming Soon
              </div>
            )}
          />
        </div>
        <div>
          <Menu.Button
            className={`${buttonBaseClasses} mt-2 border-blue-200 border-[1px] bg-transparent`}
            disabled={isExitingService}
          >
            <div className={`${backgroundDivClasses} bg-white group-hover:bg-gray-100 border border-gray-200`}></div>
            <ReactSVG
              src={getIconPath("resources", false, false)}
              className={iconClasses}
            />
            <span
              className={`absolute w-0 h-8 left-full ml-1 whitespace-nowrap flex items-center rounded-r group-hover:w-auto group-hover:pl-2 group-hover:pr-2 bg-gray-100 border border-gray-200 text-gray-800`}
            >
              Resources - Coming Soon
            </span>
          </Menu.Button>
          <NewToolTip
            id={"resourcesTooltip"}
            render={() => (
              <div className="!bg-gray-20 max-w-[460px] text-teal-300 text-body-sm p-2">
                Coming Soon
              </div>
            )}
          />
        </div>
      </Menu>
    </div>
  );
};

export default ChartNav;
